import { useCallback, useMemo, useState } from 'react';
import { Product } from '../../../types/product.types';
import { formatCurrency } from '../../../utils/formatters/money';
import { formatDateTime } from '../../../utils/formatters/date';
import { isUsableProductImage } from '../../../utils/productImage';

export interface UseProductDetailModalProps {
  product: Product | null;
  onEditProduct: (id: number) => void;
  onClose: () => void;
}

export const useProductDetailModal = ({
  product,
  onEditProduct,
  onClose,
}: UseProductDetailModalProps) => {
  // State for image expansion
  const [isImageExpanded, setIsImageExpanded] = useState(false);
  // Status helpers
  const getStatusColor = useCallback((status: string) => {
    const displayStatus = status === 'ACTIVE' || status === 'Activo' ? 'Activo' : 'Inactivo';
    return displayStatus === 'Activo' ? 'success' : 'error';
  }, []);

  const getStatusDisplay = useCallback((status: string) => {
    return status === 'ACTIVE' || status === 'Activo' ? 'Activo' : 'Inactivo';
  }, []);

  // Price formatting
  const formatPrice = useCallback((price: string | number | undefined) => {
    if (price === null || price === undefined || price === '') return formatCurrency(0);
    const n = typeof price === 'number' ? price : parseFloat(price.toString());
    if (isNaN(n)) return formatCurrency(0);
    return formatCurrency(n);
  }, []);

  // Computed values
  const statusColor = useMemo(() => {
    return product ? getStatusColor(product.status) : 'error';
  }, [product, getStatusColor]);

  const statusDisplay = useMemo(() => {
    return product ? getStatusDisplay(product.status) : 'Inactivo';
  }, [product, getStatusDisplay]);

  const sellingPrice = useMemo(() => {
    return product ? product.sellingPrice || product.price : 0;
  }, [product]);

  const formattedSellingPrice = useMemo(() => {
    return formatPrice(sellingPrice);
  }, [sellingPrice, formatPrice]);

  const formattedCostPrice = useMemo(() => {
    return product ? formatPrice(product.costPrice) : formatCurrency(0);
  }, [product, formatPrice]);

  const hasTags = useMemo(() => {
    return product?.tags && product.tags.length > 0;
  }, [product?.tags]);

  const hasImage = useMemo(() => {
    return isUsableProductImage(product?.imagePath);
  }, [product?.imagePath]);

  const formattedCreatedAt = useMemo(() => {
    const formatted = formatDateTime(product?.createdAt);
    return formatted || 'No disponible';
  }, [product?.createdAt]);

  const formattedUpdatedAt = useMemo(() => {
    const formatted = formatDateTime(product?.updatedAt);
    return formatted || 'No disponible';
  }, [product?.updatedAt]);

  // Action handlers
  const handleEditClick = useCallback(() => {
    if (product && product.id) {
      onEditProduct(product.id);
      onClose(); // Close the view modal since edit will open
    }
  }, [product, onEditProduct, onClose]);

  // Image expansion handlers
  const handleImageClick = useCallback(() => {
    if (hasImage) {
      setIsImageExpanded(true);
    }
  }, [hasImage]);

  const handleCloseImageExpansion = useCallback(() => {
    setIsImageExpanded(false);
  }, []);

  return {
    // Status
    statusColor,
    statusDisplay,
    getStatusColor,
    getStatusDisplay,

    // Formatting
    formatPrice,
    formattedSellingPrice,
    formattedCostPrice,
    formattedCreatedAt,
    formattedUpdatedAt,

    // Actions
    handleEditClick,

    // Image expansion
    isImageExpanded,
    handleImageClick,
    handleCloseImageExpansion,

    // Computed values
    hasTags,
    hasImage,
  };
};
